export interface AccessibilitySettings {
  screenReaderSupport: boolean;
  highContrast: boolean;
  keyboardNavigation: boolean;
  focusIndicators: boolean;
  reducedMotion: boolean;
  fontSize: 'small' | 'medium' | 'large' | 'extra-large';
  announcements: boolean;
}

export const DEFAULT_ACCESSIBILITY_SETTINGS: AccessibilitySettings = {
  screenReaderSupport: true,
  highContrast: false,
  keyboardNavigation: true,
  focusIndicators: true,
  reducedMotion: false,
  fontSize: 'medium',
  announcements: true
};

export class AccessibilityManager {
  private static instance: AccessibilityManager;
  private settings: AccessibilitySettings;
  private liveRegion: HTMLElement | null = null;
  private keyboardTrapStack: HTMLElement[] = [];

  constructor() {
    this.settings = this.loadAccessibilitySettings();
    this.initialize();
  }

  static getInstance(): AccessibilityManager {
    if (!AccessibilityManager.instance) {
      AccessibilityManager.instance = new AccessibilityManager();
    }
    return AccessibilityManager.instance;
  }

  private initialize(): void {
    this.setupLiveRegion();
    this.applyAccessibilitySettings();
    this.setupKeyboardNavigation();
    this.detectSystemPreferences();
  }

  private setupLiveRegion(): void {
    // Create a live region for screen reader announcements
    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.className = 'sr-only';
    this.liveRegion.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;
    document.body.appendChild(this.liveRegion);
  }

  private setupKeyboardNavigation(): void {
    // Global keyboard handler for accessibility
    document.addEventListener('keydown', (event) => {
      this.handleGlobalKeyboard(event);
    });

    // Focus management
    document.addEventListener('focusin', (event) => {
      this.handleFocusIn(event);
    });

    document.addEventListener('focusout', (event) => {
      this.handleFocusOut(event);
    });
  }

  private handleGlobalKeyboard(event: KeyboardEvent): void {
    if (!this.settings.keyboardNavigation) return;

    // Skip link functionality (Alt + 1)
    if (event.altKey && event.key === '1') {
      event.preventDefault();
      this.focusMainContent();
      this.announce('Jumped to main content');
    }

    // Help shortcut (Alt + H)
    if (event.altKey && event.key === 'h') {
      event.preventDefault();
      this.openHelp();
      this.announce('Opening help documentation');
    }

    // Settings shortcut (Alt + S)
    if (event.altKey && event.key === 's') {
      event.preventDefault();
      this.openSettings();
      this.announce('Opening settings');
    }

    // Escape key handling
    if (event.key === 'Escape') {
      this.handleEscape();
    }

    // Tab trapping for modals
    if (event.key === 'Tab') {
      this.handleTabTrapping(event);
    }
  }

  private handleFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    
    if (this.settings.focusIndicators) {
      target.classList.add('keyboard-focus');
    }

    // Announce focused element for screen readers
    if (this.settings.screenReaderSupport) {
      const announcement = this.generateFocusAnnouncement(target);
      if (announcement) {
        this.announce(announcement, 'assertive');
      }
    }
  }

  private handleFocusOut(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    target.classList.remove('keyboard-focus');
  }

  private generateFocusAnnouncement(element: HTMLElement): string | null {
    const role = element.getAttribute('role');
    const ariaLabel = element.getAttribute('aria-label');
    const title = element.getAttribute('title');
    const tagName = element.tagName.toLowerCase();
    
    let announcement = '';
    
    // Use aria-label first, then title, then text content
    const label = ariaLabel || title || element.textContent?.trim();
    
    if (!label) return null;
    
    // Add role information
    switch (role || tagName) {
      case 'button':
        announcement = `${label}, button`;
        break;
      case 'link':
        announcement = `${label}, link`;
        break;
      case 'textbox':
      case 'input': {
        const inputType = element.getAttribute('type') || 'text';
        announcement = `${label}, ${inputType} input`;
        break;
      }
      case 'checkbox': {
        const checked = (element as HTMLInputElement).checked ? 'checked' : 'unchecked';
        announcement = `${label}, checkbox, ${checked}`;
        break;
      }
      case 'tab': {
        const selected = element.getAttribute('aria-selected') === 'true' ? 'selected' : '';
        announcement = `${label}, tab, ${selected}`;
        break;
      }
      default:
        announcement = label;
    }
    
    return announcement;
  }

  private focusMainContent(): void {
    const mainContent = document.querySelector('main, [role="main"], #app-body');
    if (mainContent instanceof HTMLElement) {
      mainContent.focus();
    }
  }

  private openHelp(): void {
    // Trigger help view
    const helpBtn = document.querySelector('[data-view="help"]') as HTMLElement;
    if (helpBtn) {
      helpBtn.click();
    }
  }

  private openSettings(): void {
    // Trigger settings view
    const settingsBtn = document.querySelector('[data-view="settings"]') as HTMLElement;
    if (settingsBtn) {
      settingsBtn.click();
    }
  }

  private handleEscape(): void {
    // Close modals, menus, etc.
    const activeModal = document.querySelector('.modal.active, .dialog.active');
    if (activeModal instanceof HTMLElement) {
      const closeBtn = activeModal.querySelector('[aria-label*="close"], .close-btn');
      if (closeBtn instanceof HTMLElement) {
        closeBtn.click();
      }
    }

    // Release focus trap
    if (this.keyboardTrapStack.length > 0) {
      this.releaseFocusTrap();
    }
  }

  private handleTabTrapping(event: KeyboardEvent): void {
    if (this.keyboardTrapStack.length === 0) return;

    const currentTrap = this.keyboardTrapStack[this.keyboardTrapStack.length - 1];
    const focusableElements = this.getFocusableElements(currentTrap);
    
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = `
      button:not([disabled]),
      [href],
      input:not([disabled]),
      select:not([disabled]),
      textarea:not([disabled]),
      [tabindex]:not([tabindex="-1"]),
      details,
      summary
    `;
    
    return Array.from(container.querySelectorAll(selector))
      .filter(el => {
        const element = el as HTMLElement;
        return element.offsetParent !== null && 
               !element.hasAttribute('hidden') &&
               getComputedStyle(element).visibility !== 'hidden';
      }) as HTMLElement[];
  }

  private detectSystemPreferences(): void {
    // Detect high contrast preference
    if (window.matchMedia && window.matchMedia('(prefers-contrast: high)').matches) {
      this.updateSetting('highContrast', true);
    }

    // Detect reduced motion preference
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.updateSetting('reducedMotion', true);
    }

    // Listen for changes
    if (window.matchMedia) {
      window.matchMedia('(prefers-contrast: high)').addEventListener('change', (e) => {
        this.updateSetting('highContrast', e.matches);
      });

      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
        this.updateSetting('reducedMotion', e.matches);
      });
    }
  }

  private applyAccessibilitySettings(): void {
    const root = document.documentElement;
    
    // High contrast
    root.classList.toggle('high-contrast', this.settings.highContrast);
    
    // Reduced motion
    root.classList.toggle('reduced-motion', this.settings.reducedMotion);
    
    // Font size
    root.setAttribute('data-font-size', this.settings.fontSize);
    
    // Focus indicators
    root.classList.toggle('focus-indicators', this.settings.focusIndicators);
    
    // Apply CSS custom properties
    root.style.setProperty('--accessibility-font-scale', this.getFontScale());
  }

  private getFontScale(): string {
    switch (this.settings.fontSize) {
      case 'small': return '0.875';
      case 'large': return '1.125';
      case 'extra-large': return '1.25';
      default: return '1';
    }
  }

  // Public API methods
  announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (!this.settings.announcements || !this.liveRegion) return;
    
    this.liveRegion.setAttribute('aria-live', priority);
    this.liveRegion.textContent = message;
    
    // Clear after announcement
    setTimeout(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = '';
      }
    }, 1000);
  }

  trapFocus(element: HTMLElement): void {
    this.keyboardTrapStack.push(element);
    
    // Focus first focusable element
    const focusableElements = this.getFocusableElements(element);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  releaseFocusTrap(): HTMLElement | null {
    const released = this.keyboardTrapStack.pop();
    return released || null;
  }

  updateSetting<K extends keyof AccessibilitySettings>(
    key: K, 
    value: AccessibilitySettings[K]
  ): void {
    this.settings[key] = value;
    this.applyAccessibilitySettings();
    this.saveAccessibilitySettings();
  }

  getSettings(): AccessibilitySettings {
    return { ...this.settings };
  }

  // ARIA helpers
  setAriaLabel(element: HTMLElement, label: string): void {
    element.setAttribute('aria-label', label);
  }

  setAriaDescribedBy(element: HTMLElement, descriptorId: string): void {
    element.setAttribute('aria-describedby', descriptorId);
  }

  setAriaExpanded(element: HTMLElement, expanded: boolean): void {
    element.setAttribute('aria-expanded', expanded.toString());
  }

  setAriaSelected(element: HTMLElement, selected: boolean): void {
    element.setAttribute('aria-selected', selected.toString());
  }

  setAriaHidden(element: HTMLElement, hidden: boolean): void {
    element.setAttribute('aria-hidden', hidden.toString());
  }

  // Screen reader utilities
  makeElementAccessible(element: HTMLElement, options: {
    role?: string;
    label?: string;
    description?: string;
    expanded?: boolean;
    selected?: boolean;
    hidden?: boolean;
  }): void {
    if (options.role) element.setAttribute('role', options.role);
    if (options.label) this.setAriaLabel(element, options.label);
    if (options.description) {
      const descId = `desc-${Math.random().toString(36).substr(2, 9)}`;
      const descElement = document.createElement('span');
      descElement.id = descId;
      descElement.className = 'sr-only';
      descElement.textContent = options.description;
      element.appendChild(descElement);
      this.setAriaDescribedBy(element, descId);
    }
    if (options.expanded !== undefined) this.setAriaExpanded(element, options.expanded);
    if (options.selected !== undefined) this.setAriaSelected(element, options.selected);
    if (options.hidden !== undefined) this.setAriaHidden(element, options.hidden);
  }

  // Progress indication for screen readers
  createProgressAnnouncement(current: number, total: number, operation: string): string {
    const percentage = Math.round((current / total) * 100);
    return `${operation} progress: ${current} of ${total} completed, ${percentage}%`;
  }

  // Error announcement
  announceError(message: string): void {
    this.announce(`Error: ${message}`, 'assertive');
  }

  // Success announcement
  announceSuccess(message: string): void {
    this.announce(`Success: ${message}`, 'polite');
  }

  private loadAccessibilitySettings(): AccessibilitySettings {
    try {
      const stored = localStorage.getItem('bifrost-accessibility-settings');
      if (stored) {
        return { ...DEFAULT_ACCESSIBILITY_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to load accessibility settings:', error);
    }
    return { ...DEFAULT_ACCESSIBILITY_SETTINGS };
  }

  private saveAccessibilitySettings(): void {
    try {
      localStorage.setItem('bifrost-accessibility-settings', JSON.stringify(this.settings));
    } catch (error) {
      console.error('Failed to save accessibility settings:', error);
    }
  }

  // Cleanup
  destroy(): void {
    if (this.liveRegion && this.liveRegion.parentNode) {
      this.liveRegion.parentNode.removeChild(this.liveRegion);
    }
    this.keyboardTrapStack.length = 0;
  }
}